import { isResponseError, ResponseErrors } from 'aos-helpers/src/helpers/error/ResponseError'
import { useAuthInfo } from 'aos-mf'
import { useCreateTemplate, useUpdateTaskTemplate } from 'aos-services/src/services/tasks/templates'
import {
    initialTaskTemplate,
    TaskTemplate,
    TaskTemplateFormPayload,
} from 'aos-services/src/services/tasks/types/task/TaskTemplate'
import { notify } from 'aos-ui-universal'
import React, { ReactNode, useCallback, useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { createRegisteredContext } from 'react-singleton-context'

type TaskTemplateModalContextBaseValue = {
    isOpen: boolean
    keyPrefix: string
    taskTemplate: TaskTemplateFormPayload
}

type TaskTemplateModalContextValue = TaskTemplateModalContextBaseValue & {
    saveTemplate: (task: TaskTemplateFormPayload) => void
    isProcessing: boolean
    setTaskTemplateModal: (data: TaskTemplateModalContextBaseValue) => void
    resetMutations: () => void
    error: Error | null
}

const defaultValue: TaskTemplateModalContextValue = {
    isOpen: false,
    isProcessing: false,
    taskTemplate: initialTaskTemplate,
    keyPrefix: '',
    saveTemplate: () => undefined,
    setTaskTemplateModal: () => undefined,
    resetMutations: () => undefined,
    error: null,
}

export const TaskTemplateModalContext = createRegisteredContext<TaskTemplateModalContextValue>(
    'taskTemplateModal',
    defaultValue,
)

type TaskTemplateModalProviderProps = {
    children: ReactNode
    onTemplateSavedNavigate?: (id: number) => void
}

export const TaskTemplateModalProvider = ({
    children,
    onTemplateSavedNavigate,
}: TaskTemplateModalProviderProps) => {
    const { t } = useTranslation()
    const { siteLocation } = useAuthInfo()
    const [isOpen, setIsOpen] = React.useState(false)
    const onSuccess = useCallback(
        (isEdit?: boolean) =>
            ({ id }: TaskTemplate) => {
                setIsOpen(false)
                onTemplateSavedNavigate?.(id)
                notify({
                    status: 'success',
                    message: isEdit
                        ? t('task-template.update.toast.success.title', { ns: 'tasks' })
                        : t('task-template.create.toast.success.title', { ns: 'tasks' }),
                })
            },
        [onTemplateSavedNavigate, t],
    )
    const onError = useCallback(
        (e: Error) => {
            if (
                isResponseError(e) &&
                (e.errorPayload?.errors ?? []).includes(ResponseErrors.TASK_TEMPLATE_NAME_CONFLICT)
            ) {
                notify({
                    status: 'critical',
                    message: t('task-template.create.toast.error.TEMPLATE_NAME_CONFLICT.title', {
                        ns: 'tasks',
                    }),
                })
            } else {
                notify({
                    status: 'critical',
                    message: t('error.something-went-wrong', { ns: 'common' }),
                })
            }
        },
        [t],
    )
    const createTemplate = useCreateTemplate(siteLocation)
    const updateTemplate = useUpdateTaskTemplate()

    const error = useMemo(
        () => createTemplate.error || updateTemplate.error,
        [createTemplate, updateTemplate],
    )
    const resetMutations = useCallback(() => {
        createTemplate.reset()
        updateTemplate.reset()
    }, [createTemplate, updateTemplate])

    const isProcessing = useMemo(
        () => createTemplate.isPending || updateTemplate.isPending,
        [createTemplate, updateTemplate],
    )

    const [taskTemplate, setTaskTemplate] =
        React.useState<TaskTemplateFormPayload>(initialTaskTemplate)
    const [keyPrefix, setKeyPrefix] = React.useState<string>('')

    const saveTemplate = useCallback(
        (task: TaskTemplateFormPayload) =>
            task.id
                ? updateTemplate.mutate({ ...task }, { onSuccess: onSuccess(true), onError })
                : createTemplate.mutate(task, { onSuccess: onSuccess(false), onError }),
        [createTemplate, onError, onSuccess, updateTemplate],
    )

    const setTaskTemplateModal = useCallback(
        (data: TaskTemplateModalContextBaseValue) => {
            resetMutations()
            setIsOpen(data.isOpen)
            setTaskTemplate(data.taskTemplate ?? initialTaskTemplate)
            setKeyPrefix(data.keyPrefix)
        },
        [resetMutations],
    )

    const value = useMemo(
        () => ({
            isOpen,
            isProcessing,
            taskTemplate,
            keyPrefix,
            setTaskTemplateModal,
            saveTemplate,
            resetMutations,
            error,
        }),
        [
            error,
            isOpen,
            isProcessing,
            taskTemplate,
            keyPrefix,
            setTaskTemplateModal,
            saveTemplate,
            resetMutations,
        ],
    )

    return (
        <TaskTemplateModalContext.Provider value={value}>
            {children}
        </TaskTemplateModalContext.Provider>
    )
}

export const useTaskTemplateModalContext = (): TaskTemplateModalContextValue => {
    const ctx = useContext(TaskTemplateModalContext)
    return ctx
}
